<template>
  <div class="home">
  <!-- Hero Section -->
  <section class="hero">
    <div class="hero-content">
      <div class="hero-image">
        <img src="@/assets/uveffectsions.jpg" alt="UV Effects Pedals" />
      </div>

      <div class="hero-text">
        <h1>Unique Guitar Pedals, Built by Hand</h1>
        <p>Each UV Effects pedal is one-of-a-kind, built by hand using high-quality hardware and carefully selected New Old Stock (NOS) silicon and germanium components. Every circuit is custom-tuned for tone, responsiveness, and character.</p>
      </div>
      
    </div>
  </section>

    <!-- YouTube Video Section -->
    <section class="video-section">
      <div class="container">
        <h2 class="section-title">ION Fuzz Overdrive Demo</h2>
        <div class="video-container">
          <iframe 
            src="https://www.youtube.com/embed/2o_rCrXClX8" 
            title="UV Effects ION Fuzz Overdrive Demo"
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            allowfullscreen>
          </iframe>
        </div>
      </div>
    </section>

    <!-- What Makes UV Effects Different -->
    <section class="features">
      <div class="container">
        <h2 class="section-title">What Makes UV Effects Different</h2>
        
        <div class="features-grid">
          <div class="feature">
            <h3>Build Philosophy</h3>
            <p>
              UV Effects pedals are built using only the highest quality hardware and
              components available. Each unit is assembled by hand with through-hole construction and
              tested for tone and durability. The design is rugged and intentionally simple,
              focused entirely on sound.
            </p>
          </div>
          
          <div class="feature">
            <h3>Boutique Components</h3>
            <p>
              Each circuit features NOS silicon and germanium transistors, vintage germanium
              diodes, and high-grade op amps, known for their musical character and tonal
              response. Every part is carefully-sourced, measured, and selected.
            </p>
          </div>
          
          <div class="feature">
            <h3>Every Pedal Is Unique</h3>
            <p>
              Every UV Effects pedal is built with a different set of transistors, diodes, and
              op amps. Resistors and capacitors are hand-selected to set the right bias and
              shape the tone, ensuring the circuit performs its best with the chosen
              components.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ION Fuzz Overdrive Section -->
    <section class="product-showcase ion-section">
      <div class="container">
        <div class="product-content">
          <div class="product-text">
            <h2>The ION Fuzz Overdrive</h2>
            <p>
              A responsive, two-channel gain pedal combining a vintage one-knob fuzz and an
              amp-like overdrive in a compact enclosure. Each ION is built with a unique
              combination of NOS transistors, germanium diodes, and op amps—components found in some
              of the most iconic pedals ever made.
            </p>
            <router-link to="/pedals" class="shop-button">Learn More</router-link>
          </div>
          <div class="product-image">
            <img src="@/assets/uveffectsionamp.jpg" alt="ION Fuzz Overdrive" />
          </div>
        </div>
      </div>
    </section>

    <!-- ION PRO Section -->
    <section class="product-showcase ion-pro-section">
      <div class="container">
        <div class="product-content reverse">
          <div class="product-text">
            <h2>The ION PRO Fuzz Overdrive</h2>
            <p>
              The ION PRO offers the same responsive fuzz and amp-like overdrive as the ION,
              with added fuzz side controls for gain and bias. Built with unique combinations
              of NOS silicon transistors, germanium diodes, and high-quality op amps, each ION
              PRO is individually tuned for tonal flexibility and character.
            </p>
            <router-link to="/pedals" class="shop-button">Learn More</router-link>
          </div>
          <div class="product-image">
            <img src="@/assets/uvionpro1.jpg" alt="ION PRO Fuzz Overdrive" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'HomeView'
}
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section */
.hero {
  background: #fff;
  padding: 20px 20px;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 60px;
}

.hero-text {
  flex: 1;
}

.hero-text h1 {
  font-size: 3rem;
  font-weight: 400;
  margin-bottom: 20px;
  color: var(--text-primary);
  line-height: 1.2;
}

.hero-text p {
  font-size: 1.2rem;
  line-height: 1.6;
  color: var(--text-secondary);
}

.hero-image {
  flex: 1;
}

.hero-image img {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.ion-pro-section {
  background: #f8f9fa;
}

@media (max-width: 768px) {
  .hero-content {
    flex-direction: column;
    text-align: center;
  }
  
  .hero-text h1 {
    font-size: 2rem;
  }
}

/* YouTube Video Section */
.video-section {
  padding: 80px 20px;
  background: #fff;
}

.video-section .section-title {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 60px;
  color: var(--text-primary);
}

.video-container {
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  overflow: hidden;
}

.video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 10px;
}

/* Features Section */
.features {
  padding: 80px 20px;
  background: #f8f9fa;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 60px;
  color: var(--text-primary);
  font-weight: 400;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
}

.feature {
  text-align: center;
  padding: 30px;
}

.feature h3 {
  font-size: 1.5rem;
  margin-bottom: 20px;
  color: var(--text-primary);
  font-weight: 400;
}

.feature p {
  color: var(--text-secondary);
  line-height: 1.6;
}

/* Product Showcase Sections */
.product-showcase {
  padding: 80px 20px;
}

.product-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.product-content.reverse {
  direction: rtl;
}

.product-content.reverse > * {
  direction: ltr;
}

.product-text h2 {
  font-size: 2.2rem;
  margin-bottom: 20px;
  color: var(--text-primary);
  font-weight: 400;
}

.product-text p {
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 30px;
  font-size: 1.1rem;
}

.product-image {
  display: flex;
  justify-content: center;
  align-items: center;
}

.product-image img {
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: 10px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-text h1 {
    font-size: 2rem;
  }
  
  .product-content {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
}
</style>
